import React, { useState } from "react"
import {
	Grid,
	Typography,
	Button,
	Chip,
	Link,
	PropTypes,
	CircularProgress
} from "@material-ui/core"
import {
	Done as PluginEnabledIcon,
	HighlightOff as PluginDisabledIcon
} from "@material-ui/icons"

import { Notification, Portlet, PopConfirm } from "@/components"
import PluginGroupPermissionGate from "@/components/PermissionGate/PluginGroupPermissionGate"

import ApiService from "@/services/Api"

import useStyles from "@/pages/Admin/Plugins/GoogleDrivePlugin/styles"
import { colors } from "@/styles/colors"

import { ReactComponent as TipsIcon } from "@/assets/icons/tips_icon.svg"

import { PluginSettings } from "@/pages/Admin/Plugins/GoogleDrivePlugin"

import { letalkLinks } from "@/utils/link"
import { waitUntil } from "@/utils/time"

type SettingsProps = {
	plugin: PluginSettings
	onOAuthPopupClosed: () => Promise<void>
	onPluginUninstalled: () => Promise<void>
}

type ComponentConfig = {
	chip: {
		label: string
		className: string
		icon: React.ReactElement
	}
	button: {
		className?: string
		color?: PropTypes.Color
		onClick: () => Promise<void>
		text: string
	}
	tip?: React.ReactElement
}

const Settings: React.FC<SettingsProps> = (props) => {
	const {
		plugin,
		onOAuthPopupClosed,
		onPluginUninstalled
	} = props

	const [loading, setLoading] = useState(false)

	const classes = useStyles()

	const handleUninstallPlugin = async () => {
		PopConfirm.open({
			title: "Desinstalar plugin do Google Drive",
			description: "Deseja realmente desinstalar este plugin? Você pode instalá-lo novamente quando quiser, mas algumas configurações podem ser perdidas.",
			onConfirm: async () => {
				try {
					await ApiService.delete("/plugin-settings/google-drive/auth-credentials", {})

					await onPluginUninstalled()
				} catch (error) {
					Notification.error({ message: "Não foi possível desinstalar o plugin." })
				}
			},
			confirmButtonText: "DESINSTALAR"
		})
	}

	const handleInstallPlugin = async () => {
		try {
			setLoading(true)
			const response = await ApiService.get<{ url: string }>("/plugin-settings/google-drive/oauth-url")
			setLoading(false)

			const OAUTH_POPUP_CONFIG = {
				width: 500,
				height: 600,
				left: window.screenX + (window.outerWidth - 500) / 2,
				top: window.screenY + (window.outerHeight - 600) / 2
			}

			const popup = window.open(response.data.url, "google-drive-oauth", `width=${OAUTH_POPUP_CONFIG.width},height=${OAUTH_POPUP_CONFIG.height},left=${OAUTH_POPUP_CONFIG.left},top=${OAUTH_POPUP_CONFIG.top}`)

			/**
			 * WARNING:
			 * - Use an interval to check whether the popup has been closed, as there
			 * is no callback or event listener available on the popup window after the OAuth completes.
			 */
			await waitUntil(() => Boolean(popup?.closed))
			setLoading(true)
			await onOAuthPopupClosed()
			setLoading(false)
		} catch (error) {
			Notification.error({ message: "Não foi possível instalar o plugin." })
		}

		setLoading(false)
	}

	const getComponentConfig = (): ComponentConfig => {
		const pluginStatusToComponentConfig: Record<PluginSettings["status"], ComponentConfig> = {
			enabled: {
				chip: {
					label: "Plugin Instalado",
					className: classes.authPluginStatus,
					icon: <PluginEnabledIcon style={{ color: colors.gray[0] }}/>
				},
				button: {
					onClick: handleUninstallPlugin,
					text: "Desinstalar",
					className: classes.authPluginButton
				},
				tip: (
					<Grid
						item
						xs={12}
					>
						<Grid
							container
							className={classes.tipsContainer}
						>
							<Grid
								item
								xs={12}
							>
								<Grid
									container
									alignItems="center"
									spacing={1}
								>
									<Grid item>
										<Grid
											container
											alignItems="center"
										>
											<TipsIcon className={classes.tipsIcon} />
										</Grid>
									</Grid>

									<Grid item>
										<Grid
											container
											alignItems="center"
										>
											<Typography className={classes.tipsHeaderText} >
												Dica
											</Typography>
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							<Grid
								item
								xs={12}
							>
								<Typography
									className={classes.tipsContentText}
								>
									Agora que o seu plugin está instalado, você pode usar o Google Drive ({ plugin.settings.email }) na Letalk.
									<br />
									<Link
										href={letalkLinks.wikiHowToUseAgentsWithGoogleDriveByConnectors}
										underline="always"
										target="_blank"
										color="inherit"
									>
										Clique aqui e aprenda a como fazer
									</Link>
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				)
			},
			disabled: {
				chip: {
					label: "Plugin Desinstalado",
					className: classes.unAuthPluginStatus,
					icon: <PluginDisabledIcon/>
				},
				button: {
					onClick: handleInstallPlugin,
					text: "Instalar Plugin",
					color: "primary"
				}
			}
		}

		const componentConfig = pluginStatusToComponentConfig[plugin.status]

		if (!componentConfig) {
			return {
				chip: {},
				button: {}
			} as ComponentConfig
		}

		return componentConfig
	}

	const componentConfig = getComponentConfig()

	return (
		<Portlet>
			<Grid
				container
				direction="column"
				spacing={8}
			>
				<Grid
					item
					xs={12}
				>
					<Grid
						container
						spacing={2}
					>
						<Grid
							item
							xs={12}
						>
							<Grid
								container
								justifyContent="space-between"
							>
								<Grid item>
									<Grid
										container
										direction="column"
									>
										<Grid item>
											<Typography variant="h4">
												Acesso da API do Google Drive
											</Typography>
										</Grid>
										<Grid item>
											<Typography variant="body1">
												Conecte a sua conta do Google Drive à Letalk.
											</Typography>
										</Grid>
									</Grid>
								</Grid>

								<Grid item>
									<Chip
										size="small"
										/**
										 * WARNING:
										 * - The 'onDelete' prop is required to display the icon on the Chip component.
										 * However, no action should be triggered on click, so an empty function is
										 * passed and pointer events are disabled to prevent any interaction.
										 */
										onDelete={() => null}
										style={{ pointerEvents: "none" }}
										label={componentConfig.chip.label}
										className={componentConfig.chip.className}
										deleteIcon={componentConfig.chip.icon}
									/>
								</Grid>
							</Grid>
						</Grid>

						{componentConfig.tip}
					</Grid>
				</Grid>

				<Grid
					item
					xs={12}
				>
					<Grid
						container
						justifyContent="flex-end"
					>
						<Grid item>
							<PluginGroupPermissionGate action="disconnect" partialBlockRule={() => plugin.status !== "enabled"}>
								<Button
									variant="contained"
									className={componentConfig.button.className}
									color={componentConfig.button.color}
									onClick={componentConfig.button.onClick}
									disabled={loading}
									endIcon={loading && <CircularProgress size={20} />}
								>
									{componentConfig.button.text}
								</Button>
							</PluginGroupPermissionGate>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Portlet>
	)
}

export default Settings
